// Extra styles for the Full widget (full.ts), layered on WIDGET_CSS inside the
// same shadow root. Branding comes from the same --xq-* custom properties.

export const FULL_CSS = /* css */ `
/* ── Page: main card + progress sidebar ─────────────────────────── */
/* The wrapper is the size container, so the page inside can switch layout */
.xqf-wrap { container-type: inline-size; width: 100%; }
.xqf {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 24px;
  align-items: start;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
}
.xqf-card, .xqf-side-card {
  background: var(--xq-bg);
  border: var(--xq-canvas-border);
  border-radius: var(--xq-radius);
  box-shadow: var(--xq-shadow);
}
.xqf-card { padding: calc(var(--xq-pad) + 8px); }
/* Grid items may shrink below their content (long certificate numbers, tables) */
.xqf-main, .xqf-side, .xqf-card { min-width: 0; }
.xqf-head { margin-bottom: 20px; }
.xqf-head .xq-title { font-size: 28px; }
.xqf-section { margin-bottom: 20px; }
.xqf-section .xq-list { max-height: 320px; }
.xqf-contact { max-width: 520px; }

/* Sticky sidebar: logo, progress bar, steps with summaries */
.xqf-side { position: sticky; top: 16px; display: grid; gap: 10px; }
.xqf-side-card { overflow: hidden; }
.xqf-side-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 18px 20px 12px; }
.xqf-side-card .xq-progress { margin: 0; border-radius: 0; }
.xqf-steps { list-style: none; margin: 0; padding: 6px 0; }
.xqf-steps li > * {
  display: flex; gap: 12px; align-items: flex-start; width: 100%;
  padding: 12px 20px; border: none; background: none; font: inherit; color: inherit; text-align: left;
}
.xqf-steps li > button { cursor: pointer; }
.xqf-steps li > button:hover { background: color-mix(in srgb, var(--xq-primary) 6%, var(--xq-bg)); }
.xqf-steps li.is-current > * { background: color-mix(in srgb, var(--xq-primary) 10%, var(--xq-bg)); }
.xqf-steps li.is-todo { color: var(--xq-muted); opacity: 0.7; }
.xqf-step-icon { flex: none; width: 24px; height: 24px; margin-top: 2px; color: var(--xq-primary); }
.xqf-steps li.is-todo .xqf-step-icon { color: var(--xq-muted); }
.xqf-step-icon svg { width: 100%; height: 100%; }
.xqf-step-name { display: block; font-weight: 700; font-size: 16px; }
.xqf-step-sum { display: block; font-size: 13px; color: var(--xq-muted); overflow-wrap: anywhere; }
.xqf-side .xq-foot { border: none; background: none; }

/* Step footer: back link left, next button right */
.xqf-foot {
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  margin-top: 24px; padding-top: 20px; border-top: 1px solid var(--xq-border);
}
.xqf-foot .xq-btn { min-width: 160px; }
/* Estimate with quotes on: a callback or the detailed quote */
.xqf-choices { display: flex; gap: 10px; flex-wrap: wrap; justify-content: flex-end; }
.xq-btn-ghost { background: transparent; color: var(--xq-primary); box-shadow: inset 0 0 0 2px var(--xq-primary); }
.xqf-quote-hint { text-align: right; margin-top: 8px; }
.xqf-choices .xq-btn-ghost { margin-top: 0; }
.xq-done a.xq-btn { text-decoration: none; margin: 8px 0; }

/* ── Property page rows ─────────────────────────────────────────── */
.xqf-rows { display: grid; }
.xqf-row {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding: 14px 0; border-bottom: 1px solid var(--xq-border);
}
/* A required answer that's still missing: the row glows (brand accent) until answered */
.xqf-row { position: relative; }
.xqf-row.is-missing {
  margin: 6px -12px; padding: 14px 12px; border-radius: 10px; border-bottom-color: transparent;
  background: color-mix(in srgb, var(--xq-accent) 12%, var(--xq-bg));
  box-shadow: 0 0 0 2px var(--xq-accent);
  animation: xqf-glow 1.8s ease-in-out infinite;
}
@keyframes xqf-glow {
  0%, 100% { box-shadow: 0 0 0 2px var(--xq-accent), 0 0 0 0 color-mix(in srgb, var(--xq-accent) 55%, transparent); }
  50% { box-shadow: 0 0 0 2px var(--xq-accent), 0 0 18px 4px color-mix(in srgb, var(--xq-accent) 55%, transparent); }
}
/* The pulsing star on the first missing answer (as in the console) */
.xqf-need {
  position: absolute; top: -9px; left: -9px; z-index: 1;
  display: inline-flex; align-items: center; justify-content: center;
  width: 22px; height: 22px; border-radius: 50%;
  background: var(--xq-accent); color: var(--xq-primary);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--xq-accent) 40%, transparent);
  animation: xqf-star 1.4s ease-in-out infinite;
}
.xqf-need svg { width: 12px; height: 12px; }
@keyframes xqf-star { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.15); } }
.xqf-missing-note { margin: 0 0 8px; font-size: 14px; font-weight: 600; color: var(--xq-text); }
.xqf-missing-note[hidden] { display: none; }
@media (prefers-reduced-motion: reduce) { .xqf-row.is-missing, .xqf-need { animation: none; } }
.xqf-row-text { display: grid; gap: 2px; }
.xqf-row-title { font-weight: 700; font-size: 16px; }
.xqf-row-sub { font-size: 13px; color: var(--xq-muted); }
.xqf-row-control { flex: none; }
.xqf-select { min-width: 220px; height: 44px; }
.xqf-epc-tag {
  display: inline-block; margin-left: 6px; padding: 1px 6px; border-radius: 4px; vertical-align: 2px;
  font-size: 10px; font-weight: 700; letter-spacing: 0.4px; background: var(--xq-accent); color: #0B1A1E;
}

/* Stepper: − value + in the primary colour */
.xqf-stepper {
  display: inline-flex; align-items: center; gap: 4px; padding: 4px;
  border-radius: 999px; background: var(--xq-primary); color: var(--xq-primary-text);
}
.xqf-stepper button {
  width: 36px; height: 36px; border: none; border-radius: 50%; background: none;
  color: inherit; font-size: 22px; line-height: 1; cursor: pointer;
}
.xqf-stepper button:disabled { opacity: 0.4; cursor: default; }
.xqf-stepper input {
  width: 64px; border: none; background: none; color: inherit; text-align: center;
  font: inherit; font-size: 20px; font-weight: 600; -moz-appearance: textfield;
}
.xqf-stepper input::-webkit-inner-spin-button, .xqf-stepper input::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }

/* Yes / No pill */
.xqf-yesno { display: inline-flex; padding: 4px; gap: 4px; border-radius: 999px; background: color-mix(in srgb, var(--xq-border) 45%, var(--xq-bg)); }
.xqf-yesno button {
  min-width: 64px; height: 38px; border: none; border-radius: 999px; background: none;
  font: inherit; font-weight: 600; color: var(--xq-text); cursor: pointer;
}
.xqf-yesno button[aria-pressed="true"] { background: var(--xq-primary); color: var(--xq-primary-text); }

/* ── EPC card ───────────────────────────────────────────────────── */
.xqf-epc { max-width: 560px; min-width: 0; display: grid; gap: 8px; }
.xqf-epc-top, .xqf-epc-num { background: #2D6FD8; color: #FFFFFF; padding: 20px 24px; }
.xqf-epc-top { display: flex; justify-content: space-between; gap: 16px; align-items: stretch; }
.xqf-epc-addr { font-size: 17px; line-height: 1.5; }
.xqf-epc-rating { display: grid; place-items: center; min-width: 140px; padding: 10px 16px; border: 1px solid rgba(255, 255, 255, 0.9); text-align: center; }
.xqf-epc-rating span { font-size: 15px; }
.xqf-epc-rating strong { font-size: 56px; line-height: 1; }
.xqf-epc-num { display: grid; gap: 4px; }
.xqf-epc-num strong { font-size: 19px; letter-spacing: 0.3px; overflow-wrap: anywhere; }
.xqf-epc-num span:last-child:not(:first-child) { font-size: 13px; opacity: 0.85; }
.xqf-epc-chart { width: 100%; table-layout: fixed; border-collapse: collapse; margin-top: 8px; font-size: 14px; }
.xqf-epc-chart th { padding: 4px 8px; border: 1px solid var(--xq-border); text-align: left; font-weight: 700; }
.xqf-epc-chart td { padding: 0; border-left: 1px solid var(--xq-border); border-right: 1px solid var(--xq-border); height: 40px; }
.xqf-epc-chart tr:last-child td { border-bottom: 1px solid var(--xq-border); }
/* Column widths (fixed table layout takes them from the header row) */
.xqf-epc-chart th:nth-child(1) { width: 64px; }
.xqf-epc-chart th:nth-child(3), .xqf-epc-chart th:nth-child(4) { width: 92px; }
.xqf-epc-range { width: 64px; padding: 0 8px !important; font-weight: 700; color: #111; }
.xqf-epc-bar { display: flex; align-items: center; justify-content: flex-end; height: 100%; padding-right: 10px; font-size: 24px; font-weight: 800; color: #111; }
.xqf-epc-col { width: 92px; position: relative; }
.xqf-arrow {
  position: absolute; inset: 4px 6px 4px 18px; display: flex; align-items: center; justify-content: center;
  background: var(--c); color: #111; font-weight: 700; font-size: 14px;
}
.xqf-arrow::before {
  content: ""; position: absolute; left: -14px; top: 0; bottom: 0; width: 14px;
  background: var(--c); clip-path: polygon(100% 0, 0 50%, 100% 100%);
}
.xqf-btn-outline {
  justify-self: end; display: inline-flex; align-items: center; gap: 8px; margin-top: 8px;
  padding: 10px 20px; border: 1px solid var(--xq-border); border-radius: 999px;
  color: var(--xq-text); text-decoration: none; font-weight: 600;
}
.xqf-btn-outline svg { width: 18px; height: 18px; }

/* ── Grant checklist ────────────────────────────────────────────── */
.xqf-checks {
  list-style: none; margin: 20px 0 8px; padding: 20px 24px; display: grid; gap: 16px;
  border-radius: var(--xq-radius); background: var(--xq-text); color: var(--xq-bg);
}
.xqf-check { display: flex; justify-content: space-between; gap: 16px; align-items: center; }
.xqf-check strong { display: block; font-size: 17px; }
.xqf-check span { display: block; font-size: 14px; opacity: 0.85; }
.xqf-check svg { flex: none; width: 30px; height: 30px; }
.xqf-check.is-pass svg { color: #22C55E; }
.xqf-check.is-fail svg { color: #F87171; }
.xqf-check.is-info svg { color: #93C5FD; }
.xqf-choice-q { margin: 20px 0 10px; font-weight: 700; text-align: center; }
.xqf-choice { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.xqf-choice button {
  padding: 16px; border: 1px solid var(--xq-border); border-radius: var(--xq-btn-radius);
  background: var(--xq-input-bg); font: inherit; font-weight: 600; color: var(--xq-text); cursor: pointer;
}
.xqf-choice button[aria-pressed="true"] { border-color: var(--xq-primary); box-shadow: 0 0 0 2px var(--xq-primary); }

/* Estimate breakdown paragraph */
.xq-working p { margin: 8px 0; }

/* ── Floating Full: a large dialog over the page ────────────────── */
.xqf-overlay {
  position: fixed; inset: 0; z-index: 2147483000; overflow-y: auto;
  padding: 24px; background: rgba(15, 23, 42, 0.45);
}
.xq-contained .xqf-overlay { position: absolute; }

/* ── Narrow containers: sidebar becomes a compact bar above ────── */
@container (max-width: 820px) {
  .xqf { grid-template-columns: minmax(0, 1fr); }
  .xqf-card { padding: 20px 16px; }
  .xqf-head .xq-title { font-size: 22px; }
  .xqf-side { position: static; order: -1; }
  .xqf-steps { display: none; }
  .xqf-row { flex-wrap: wrap; }
  .xqf-select { min-width: 0; width: 100%; }
  .xqf-row-control { flex: 1 1 100%; }
  .xqf-choice { grid-template-columns: 1fr; }
}

/* ── Phones: the EPC card stacks and the chart tightens ────────── */
@container (max-width: 520px) {
  .xqf-epc-top { flex-direction: column; padding: 16px; }
  .xqf-epc-rating { min-width: 0; flex-direction: row; display: flex; justify-content: space-between; align-items: center; padding: 8px 14px; }
  .xqf-epc-rating strong { font-size: 40px; }
  .xqf-epc-num { padding: 16px; }
  .xqf-epc-num strong { font-size: 16px; }
  .xqf-epc-chart { font-size: 12px; }
  .xqf-epc-chart th { padding: 4px; font-size: 11px; }
  .xqf-epc-range { width: 50px; padding: 0 4px !important; }
  .xqf-epc-col { width: 58px; }
  .xqf-epc-chart th:nth-child(1) { width: 50px; }
  .xqf-epc-chart th:nth-child(3), .xqf-epc-chart th:nth-child(4) { width: 62px; }
  .xqf-epc-bar { font-size: 18px; padding-right: 6px; }
  .xqf-arrow { inset: 6px 2px 6px 12px; font-size: 12px; }
  .xqf-arrow::before { left: -10px; width: 10px; }
  .xqf-btn-outline { justify-self: stretch; justify-content: center; }
  .xqf-foot .xq-btn { min-width: 0; }
}
`;
